/**
 * The service dictionaries API (`/api/v1/service`, LIAA service plan S1). Reads are open to everyone
 * working with work orders; writes need `service.settings`. Every write carries the row `version`
 * (409 VERSION_CONFLICT on a stale one).
 */

import type {
  AbsenceInput,
  CatalogItem,
  CatalogItemInput,
  CatalogListParams,
  CatalogListResponse,
  LinkableUser,
  ResourceInput,
  ServiceResource,
  ServiceSettingKey,
  ServiceSettings,
  StaffInput,
  StaffProfile,
  StatusUpdateInput,
  WorkOrderGroup,
  WorkOrderStatus,
  WorkType,
  WorkTypeInput,
} from '../types/serviceSetupTypes';

import { apiClient } from '@/shared/api/client';

const BASE = '/service';

export async function fetchWorkTypes(includeArchived: boolean): Promise<WorkType[]> {
  const { data } = await apiClient.get<{ workTypes: WorkType[] }>(`${BASE}/work-types`, {
    params: { includeArchived: String(includeArchived) },
  });
  return data.workTypes;
}

export async function createWorkType(input: WorkTypeInput): Promise<WorkType> {
  const { data } = await apiClient.post<{ workType: WorkType }>(`${BASE}/work-types`, input);
  return data.workType;
}

export async function updateWorkType(
  id: string,
  version: number,
  input: WorkTypeInput,
): Promise<WorkType> {
  const { data } = await apiClient.patch<{ workType: WorkType }>(`${BASE}/work-types/${id}`, {
    version,
    ...input,
  });
  return data.workType;
}

export async function setWorkTypeArchived(
  id: string,
  version: number,
  archived: boolean,
): Promise<WorkType> {
  const { data } = await apiClient.post<{ workType: WorkType }>(
    `${BASE}/work-types/${id}/${archived ? 'archive' : 'restore'}`,
    { version },
  );
  return data.workType;
}

export async function fetchCatalog(params: CatalogListParams): Promise<CatalogListResponse> {
  const { data } = await apiClient.get<CatalogListResponse>(`${BASE}/catalog`, {
    params: {
      page: params.page,
      limit: params.limit,
      includeArchived: String(params.includeArchived),
      ...(params.workTypeId !== undefined && { workTypeId: params.workTypeId }),
      ...(params.q !== undefined && params.q !== '' && { q: params.q }),
    },
  });
  return data;
}

export async function createCatalogItem(input: CatalogItemInput): Promise<CatalogItem> {
  const { data } = await apiClient.post<{ item: CatalogItem }>(`${BASE}/catalog`, input);
  return data.item;
}

export async function updateCatalogItem(
  id: string,
  version: number,
  input: CatalogItemInput,
): Promise<CatalogItem> {
  const { data } = await apiClient.patch<{ item: CatalogItem }>(`${BASE}/catalog/${id}`, {
    version,
    ...input,
  });
  return data.item;
}

export async function setCatalogItemArchived(
  id: string,
  version: number,
  archived: boolean,
): Promise<CatalogItem> {
  const { data } = await apiClient.post<{ item: CatalogItem }>(
    `${BASE}/catalog/${id}/${archived ? 'archive' : 'restore'}`,
    {
      version,
    },
  );
  return data.item;
}

export async function fetchStaff(includeInactive: boolean): Promise<StaffProfile[]> {
  const { data } = await apiClient.get<{ staff: StaffProfile[] }>(`${BASE}/staff`, {
    params: { includeInactive: String(includeInactive) },
  });
  return data.staff;
}

export async function createStaff(input: StaffInput): Promise<StaffProfile> {
  const { data } = await apiClient.post<{ staff: StaffProfile }>(`${BASE}/staff`, input);
  return data.staff;
}

export async function updateStaff(
  id: string,
  version: number,
  input: StaffInput,
): Promise<StaffProfile> {
  const { data } = await apiClient.patch<{ staff: StaffProfile }>(`${BASE}/staff/${id}`, {
    version,
    ...input,
  });
  return data.staff;
}

export async function addAbsence(staffId: string, input: AbsenceInput): Promise<StaffProfile> {
  const { data } = await apiClient.post<{ staff: StaffProfile }>(
    `${BASE}/staff/${staffId}/absences`,
    input,
  );
  return data.staff;
}

export async function updateAbsence(
  staffId: string,
  absenceId: string,
  input: AbsenceInput,
): Promise<StaffProfile> {
  const { data } = await apiClient.patch<{ staff: StaffProfile }>(
    `${BASE}/staff/${staffId}/absences/${absenceId}`,
    input,
  );
  return data.staff;
}

export async function removeAbsence(staffId: string, absenceId: string): Promise<StaffProfile> {
  const { data } = await apiClient.delete<{ staff: StaffProfile }>(
    `${BASE}/staff/${staffId}/absences/${absenceId}`,
  );
  return data.staff;
}

export async function fetchLinkableUsers(): Promise<LinkableUser[]> {
  const { data } = await apiClient.get<{ users: LinkableUser[] }>(`${BASE}/staff/linkable-users`);
  return data.users;
}

export async function fetchResources(): Promise<ServiceResource[]> {
  const { data } = await apiClient.get<{ resources: ServiceResource[] }>(`${BASE}/resources`);
  return data.resources;
}

export async function fetchResourceLocations(): Promise<Array<{ id: string; name: string }>> {
  const { data } = await apiClient.get<{ locations: Array<{ id: string; name: string }> }>(
    `${BASE}/resources/locations`,
  );
  return data.locations;
}

export async function createResource(input: ResourceInput): Promise<ServiceResource> {
  const { data } = await apiClient.post<{ resource: ServiceResource }>(`${BASE}/resources`, input);
  return data.resource;
}

export async function updateResource(
  id: string,
  version: number,
  input: ResourceInput,
): Promise<ServiceResource> {
  const { data } = await apiClient.patch<{ resource: ServiceResource }>(`${BASE}/resources/${id}`, {
    version,
    ...input,
  });
  return data.resource;
}

export async function reorderResources(ids: string[]): Promise<ServiceResource[]> {
  const { data } = await apiClient.post<{ resources: ServiceResource[] }>(
    `${BASE}/resources/reorder`,
    { ids },
  );
  return data.resources;
}

export async function fetchStatuses(): Promise<WorkOrderStatus[]> {
  const { data } = await apiClient.get<{ statuses: WorkOrderStatus[] }>(`${BASE}/statuses`);
  return data.statuses;
}

export async function createStatus(input: {
  group: WorkOrderGroup;
  nameLv: string;
  nameEn: string;
  timeLimitHours: number | null;
  commentRequired: boolean;
}): Promise<WorkOrderStatus> {
  const { data } = await apiClient.post<{ status: WorkOrderStatus }>(`${BASE}/statuses`, input);
  return data.status;
}

export async function updateStatus(id: string, input: StatusUpdateInput): Promise<WorkOrderStatus> {
  const { data } = await apiClient.patch<{ status: WorkOrderStatus }>(
    `${BASE}/statuses/${id}`,
    input,
  );
  return data.status;
}

export async function setStatusArchived(
  id: string,
  version: number,
  archived: boolean,
): Promise<WorkOrderStatus> {
  const { data } = await apiClient.post<{ status: WorkOrderStatus }>(
    `${BASE}/statuses/${id}/${archived ? 'archive' : 'restore'}`,
    {
      version,
    },
  );
  return data.status;
}

export async function moveStatus(
  id: string,
  version: number,
  direction: 'up' | 'down',
): Promise<WorkOrderStatus[]> {
  const { data } = await apiClient.post<{ statuses: WorkOrderStatus[] }>(
    `${BASE}/statuses/${id}/move`,
    { version, direction },
  );
  return data.statuses;
}

export async function fetchServiceSettings(): Promise<ServiceSettings> {
  const { data } = await apiClient.get<{ values: ServiceSettings }>(`${BASE}/settings`);
  return data.values;
}

export async function updateServiceSettings(
  values: Partial<Record<ServiceSettingKey, string>>,
): Promise<ServiceSettings> {
  const { data } = await apiClient.put<{ values: ServiceSettings }>(`${BASE}/settings`, { values });
  return data.values;
}
